import type { VariantProps } from 'class-variance-authority'
import type { badgeVariants } from '@/components/ui/badge'
import type { TicketStatus, TicketPriority } from '@/types/ticket'
import type { TrackerStatus, DevStage } from '@/types/tracker'

type BadgeVariant = NonNullable<VariantProps<typeof badgeVariants>['variant']>

export const statusLabels: Record<TicketStatus, string> = {
  open: 'Open',
  in_progress: 'In Progress',
  pending: 'Awaiting You',
  resolved: 'Resolved',
  closed: 'Closed',
}

export const priorityLabels: Record<TicketPriority, string> = {
  low: 'Low',
  normal: 'Normal',
  high: 'High',
}

// Colour follows the mockup's status pills (Tickets inbox screen):
// Open=info(cyan), In Progress=default(green tint), Awaiting You=warning
// (amber), Resolved=success(lime), Closed=neutral(gray).
export const statusVariants: Record<TicketStatus, BadgeVariant> = {
  open: 'info',
  in_progress: 'default',
  pending: 'warning',
  resolved: 'success',
  closed: 'neutral',
}

const ADMIN_TRANSITIONS: Record<TicketStatus, TicketStatus[]> = {
  open: ['in_progress', 'pending', 'resolved'],
  in_progress: ['pending', 'resolved', 'open'],
  pending: ['in_progress', 'resolved', 'open'],
  resolved: ['closed', 'open'],
  closed: ['open'],
}

export function nextStatuses(current: TicketStatus): TicketStatus[] {
  return ADMIN_TRANSITIONS[current] ?? []
}

export const trackerStatusLabels: Record<TrackerStatus, string> = {
  new: 'New',
  needs_info: 'Needs info',
  accepted: 'Accepted',
  proposed_closed: 'Proposed closed',
  closed: 'Closed',
  on_hold: 'On hold',
  wont_do: "Won't do",
}

// Colour follows the mockup's Client Tracker "Status" pills: Accepted=default
// (green tint, matching "In progress" on the Tickets screen), Proposed
// closed=warning(amber). The rest have no direct mockup example; new/closed/
// on_hold read as neutral, needs_info as warning (needs attention), wont_do
// stays destructive.
export const trackerStatusVariants: Record<TrackerStatus, BadgeVariant> = {
  new: 'neutral',
  needs_info: 'warning',
  accepted: 'default',
  proposed_closed: 'warning',
  closed: 'neutral',
  on_hold: 'neutral',
  wont_do: 'destructive',
}

export const devStageLabels: Record<DevStage, string> = {
  not_scoped: 'Not scoped',
  scoped: 'Scoped',
  planned: 'Planned',
  in_progress: 'In progress',
  shipped: 'Shipped',
  cancelled: 'Cancelled',
}

// Colour follows the mockup's Client Tracker "Progress" pills: In
// progress=info(cyan), Shipped=success(lime), Not scoped=neutral(gray).
export const devStageVariants: Record<DevStage, BadgeVariant> = {
  not_scoped: 'neutral',
  scoped: 'neutral',
  planned: 'neutral',
  in_progress: 'info',
  shipped: 'success',
  cancelled: 'destructive',
}

export const trackerStatusOrder: TrackerStatus[] = [
  'new',
  'needs_info',
  'accepted',
  'proposed_closed',
  'closed',
  'on_hold',
  'wont_do',
]
